import React, { ReactNode } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import CascadingSidePanel from '../../common/CascadingSidePanel';

export interface ReusableSidebarProps {
  /**
   * Whether the sidebar is open
   */
  isOpen: boolean;
  
  /**
   * Callback when sidebar is closed
   */
  onClose: () => void;
  
  /**
   * Callback when all panels should be closed (optional)
   */
  onCloseAll?: () => void;
  
  /**
   * Unique identifier for this sidebar panel
   * Required for proper cascading panel management
   */
  panelId: string;
  
  /**
   * Title to display in the sidebar header
   */
  title?: string;
  
  /**
   * Dynamic content to render inside the sidebar
   * Can be any React node or component
   */
  children: ReactNode;
  
  /**
   * Show back button in header (optional)
   */
  showBackButton?: boolean;
  
  /**
   * Callback when back button is clicked (optional)
   */
  onBack?: () => void;
  
  /**
   * Custom header content (optional)
   * If provided, overrides the default header with title/close button
   */
  headerContent?: ReactNode;
  
  /**
   * Custom footer content (optional)
   */
  footerContent?: ReactNode;
  
  /**
   * Whether to show divider after header (default: true)
   */
  showHeaderDivider?: boolean;
  
  /**
   * Whether to show divider before footer (default: true)
   */
  showFooterDivider?: boolean;
  
  /**
   * Custom CSS styles for the content area (optional)
   */
  contentStyles?: React.CSSProperties;
  
  /**
   * Custom CSS styles for the header (optional)
   */
  headerStyles?: React.CSSProperties;
  
  /**
   * Custom CSS styles for the footer (optional)
   */
  footerStyles?: React.CSSProperties;
  
  /**
   * Maximum height for content area (optional)
   * Useful for scrollable content
   */
  maxContentHeight?: string | number;
  
  /**
   * Whether content should be scrollable (default: true)
   */
  scrollable?: boolean;
}

/**
 * ReusableSidebar - A flexible, reusable sidebar component that can be used anywhere
 * 
 * Features:
 * - Dynamic content support
 * - Cascading panel support (multiple sidebars side-by-side)
 * - Customizable header and footer
 * - Responsive design
 * - Smooth animations
 * 
 * @example
 * ```tsx
 * <ReusableSidebar
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   panelId="my-sidebar"
 *   title="My Sidebar"
 * >
 *   <div>Your content here</div>
 * </ReusableSidebar>
 * ```
 */
const ReusableSidebar: React.FC<ReusableSidebarProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  panelId,
  title,
  children,
  showBackButton = false,
  onBack,
  headerContent,
  footerContent,
  showHeaderDivider = true,
  showFooterDivider = true,
  contentStyles,
  headerStyles,
  footerStyles,
  maxContentHeight,
  scrollable = true,
}) => {
  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Default header if no custom header provided
  const defaultHeader = (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: showHeaderDivider ? '1px solid' : 'none',
        borderColor: 'divider',
        ...headerStyles,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        {showBackButton && (
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{ mr: 1 }}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {title && (
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {title}
          </Typography>
        )}
      </Box>
      <IconButton
        onClick={handleClose}
        size="small"
        aria-label="Close sidebar"
        sx={{
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      onCloseAll={onCloseAll}
      panelId={panelId}
      title={title}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        {headerContent || (title || showBackButton) ? (
          <>
            {headerContent || defaultHeader}
            {showHeaderDivider && !headerContent && (
              <Divider />
            )}
          </>
        ) : null}

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: scrollable ? 'auto' : 'visible',
            p: 2,
            maxHeight: maxContentHeight,
            ...contentStyles,
          }}
        >
          {children}
        </Box>

        {/* Footer */}
        {footerContent ? (
          <>
            {showFooterDivider && <Divider />}
            <Box
              sx={{
                p: 2,
                borderTop: showFooterDivider ? '1px solid' : 'none',
                borderColor: 'divider',
                ...footerStyles,
              }}
            >
              {footerContent}
            </Box>
          </>
        ) : null}
      </Box>
    </CascadingSidePanel>
  );
};

export default ReusableSidebar;

